import {
  red,
  orange,
  lightBlue,
  grey,
  blue,
  lightGreen,
  cyan,
  pink,
  indigo,
  yellow,
} from '@mui/material/colors'
import { createTheme } from '@mui/material/styles'
import { deDE } from '@mui/x-data-grid/locales'
import { sceneConfig } from '@modugen/ui-toolkit'

const baseTheme = createTheme()

const isCypress = typeof window !== 'undefined' && 'Cypress' in window

const theme = createTheme(
  {
    palette: {
      primary: {
        lighter: '#D7E6D6',
        light: '#4da749',
        main: '#378434',
        dark: '#236522',
        contrastText: '#fff',
      },
      secondary: {
        dark: '#E5E5E5',
        main: '#FAFAFA',
        contrastText: '#000',
      },
      action: {
        selected: 'rgba(0, 0, 0, 0.15)',
      },
    },

    icons: {
      toolbar: '#545454',
    },

    scenePalette: {
      selection: sceneConfig.ElementSelectedColor,
      hover: '#00ffb3',
      invalid: red[500],
      warning: yellow[500],
      outlines: '#ffffff',
      outlinesDark: '#000000',
      helpers: '#3832a8',
      highlight: '#a6d0ff',
      highlightElements: '#81ff6e',
      highlightDark: '#00008b',

      elements3d: {
        walls: '#98a2ab',
        openings: '#3777ff',
        slabs: grey[100],
        vertical_slabs: blue[200],
        roof_slabs: grey[100],
        vertical_roof_slabs: cyan[100],
        beams: '#F5C21B',
        steelBeams: '#A1B0C6',
        columns: '#F5C21B',
        rips: '#f5c21b',
        supports: '#39FF14',
        targets: '#5689ff',
        stiffening: '#66a369',
        member: '#f5c21b',
        ripStepSize: '#3853d9',
        foundation: '#444444',
        externalWalls: grey[500],
        internalWalls: grey[300],
      },

      /** IFC element type colors (GLB model mesh + form). Configurable independently from elements3d. */
      ifc: {
        inner_walls: indigo[300],
        outer_walls: indigo[500],
        slabs: lightBlue[300],
        roof_slabs: pink[400],
        roofs: pink[400],
        columns: yellow[400],
        beams: yellow[400],
        openings: orange[200],
        doors: orange[200],
        windows: orange[200],
        members: '#f5c21b',
        stairs: grey[300],
        furniture: grey[300],
        unclassified: grey[500],
      },

      elements2d: {
        externalWalls: {
          color: '#000000',
          width: 3,
        },
        internalWalls: {
          color: grey[600],
          width: 3,
        },
        openings: cyan[400],
        slabs: {
          color: '#FFFFFF',
          outlineColor: lightGreen[500],
          outlineWidth: 2,
        },
        vertical_slabs: {
          color: blue[100],
          outlineColor: '#2512FF',
          outlineWidth: 1,
        },
        roof_slabs: {
          color: red[100],
          outlineColor: '#FF0000',
          outlineWidth: 2,
        },
        vertical_roof_slabs: {
          color: cyan.A100,
          outlineColor: cyan[900],
          outlineWidth: 1,
        },
        beams: {
          color: '#FF00FF',
          width: 1.5,
          dashSize: 4,
          gap: 2,
          dashScale: 20,
          dashed: true,
        },
        columns: {
          color: lightBlue[100],
          outlineColor: '#8001FF',
          outlineWidth: 1.5,
        },
        purlins: {
          color: '#FF00FF',
          width: 1.5,
          dashSize: 4,
          gap: 2,
          dashScale: 20,
          dashed: true,
        },
        rips: {
          color: orange[50],
          outlineColor: '#FF8001',
          outlineWidth: 1.5,
        },
        areaLoads: {
          color: blue[600],
          outlines: {
            color: blue[900],
            width: 2,
            dashSize: 1,
            gap: 1,
            dashScale: 15,
            dashed: true,
          },
        },
      },

      openings: {
        selected: '#16de52',
        removed: '#f03535',
      },

      representatives: {
        representative: '#297500',
        representativeFor: '#7ded40',
      },
      planArea: {
        frame: '#6A1B9A',
        margin: '#6A1B9A',
      },
      background2D: '#FFFFFF',
      background3D: grey[50],
    },

    sceneOffsets: {
      verticalSlabs: {
        z: 0.01,
      },
      verticalRoofSlabs: {
        z: 0.01,
      },
    },

    sceneConfig: {
      defaultThickness: 0.005,
    },

    shape: {
      borderRadius: 4,
    },

    distances: {
      editorDrawerWidth: 455,
      editorDrawerWidthExpanded: 900,
      navBarHeight: isCypress ? '64px' : '48px',
      navBarHeightExpanded: isCypress ? '64px' : '56px',
      bottomDrawerHeight: '350px',
      bottomDrawerHeightExpanded: '700px',
      bottomDrawerInactiveHeight: '30px',
    },

    typography: {
      fontSize: 13, // influences all rem based values

      h1: {
        fontSize: '2.5rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
      subtitle1: {
        fontSize: '1rem',
      },
    },

    results: {
      utilization: {
        underutilized: {
          main: 'lightgreen',
        },
        slightlyOverutilized: {
          main: orange[500],
          light: orange[100],
        },
        overutilized: {
          main: red.A200,
          light: red[100],
        },
      },
    },

    // WARNING: overrides apparently do not get hot-reloaded, you need to
    // manually reload the application to see the changes
    components: {
      MuiCollapse: {
        styleOverrides: {
          root: {
            '& .SnackbarItem-variantSuccess': {
              background: 'white !important',
              color: `${baseTheme.palette.text.primary} !important`,

              '& svg': {
                color: `${baseTheme.palette.success.main} !important`,
              },
            },
            '& .SnackbarItem-variantError': {
              background: 'white !important',
              color: `${baseTheme.palette.text.primary} !important`,

              '& svg': {
                color: `${baseTheme.palette.error.main} !important`,
              },
            },
          },
        },
      },

      MuiButtonBase: {
        defaultProps: {
          // disableRipple: true,
        },
      },

      MuiButton: {
        defaultProps: {
          // disableRipple: true,
        },
      },

      MuiAccordion: {
        defaultProps: {
          elevation: 0,
          square: true,
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${baseTheme.palette.grey[200]}`,
            '&:first-of-type': {
              borderTopLeftRadius: 4,
              borderTopRightRadius: 4,
            },
            '&:last-of-type': {
              borderBottomLeftRadius: 4,
              borderBottomRightRadius: 4,
            },
            '&:not(:last-child)': {
              borderBottom: 0,
            },
            '&:before': {
              display: 'none',
            },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },

      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            padding: baseTheme.spacing(1.5, 1, 1.5, 1.5),
            minHeight: 'initial',
          },

          content: {
            margin: 0,
            lineHeight: 1,
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            '&.Mui-selected': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.primary.lighter,
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            },
            '&.Mui-selected.Mui-disabled': {
              color: theme.palette.action.disabled,
              backgroundColor: theme.palette.action.disabledBackground,
            },
          }),
        },
      },

      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: baseTheme.spacing(0, 1, 1, 1),
          },
        },
      },
    },
  },
  deDE,
)

export default theme
